import * as THREE from 'three';
import type { ShipSpec } from './shipSpec';

/**
 * A drive plume drawn as glowing gas rather than as a mesh.
 *
 * The mesh is only a proxy: the back faces of a frustum that bounds the plume.
 * Each fragment casts the camera ray through it and integrates the emission
 * of a sum of 3D Gaussians along that ray in closed form (erf), so the plume
 * has no silhouette from any angle - its edge is where the gas thins out, not
 * where a triangle ends. The terms:
 *
 *   core    a chain of narrow Gaussians down the axis, widening and dimming
 *   halo    a wider, fainter chain around it, running further
 *   ring    a difference of Gaussians at the exit plane: the bright annulus a
 *           Hall or MPD channel shows end-on
 *   throat  a small blob at the exit, the hot mouth seen up the nozzle
 *   knots   shock diamonds, chemical engines only
 *
 * Nothing here is lit, nothing reads a texture, and the program is the same
 * for every plume, so a plume that lights up mid-flight compiles nothing.
 * Time is the render clock handed to `update`; nothing in a fight's outcome
 * reads it.
 */

/**
 * What kind of engine a nozzle is, which sets its shape and colour.
 *   mpd     magnetoplasmadynamic (Custos, c.2080): thin, faint, violet
 *   nep     fission-electric cluster (Cannibal Heavy): wide, diffuse, pale
 *   fusion  first-generation p-11B fusion-electric (Spine & Truss, Lance):
 *           brighter blue-violet with a hard white core and a bright ring
 *   dfd     direct fusion drive, D-He3 out of a magnetic nozzle (Iuventius Pup):
 *           a narrow deep-blue blade, a ring where the field lets go of it
 *   chem    bipropellant sprint booster: warm, wide vacuum flare, shock knots
 *   rcs     chemical attitude jet: the same, short and pale
 */
export type PlumeFamily = 'mpd' | 'nep' | 'fusion' | 'dfd' | 'chem' | 'rcs';
export const PLUME_FAMILIES: readonly PlumeFamily[] = ['mpd', 'nep', 'fusion', 'dfd', 'chem', 'rcs'];

/** How loud the whole drive reads: `cinematic` is the game's default. */
export type PlumeLook = 'subtle' | 'cinematic' | 'vivid';
export const PLUME_LOOKS: readonly PlumeLook[] = ['subtle', 'cinematic', 'vivid'];

export function parsePlumeLook(raw: string | null | undefined): PlumeLook {
  return raw === 'subtle' || raw === 'vivid' ? raw : 'cinematic';
}

interface FamilyShape {
  /** Linear RGB. */
  core: [number, number, number];
  halo: [number, number, number];
  ring: [number, number, number];
  knot: [number, number, number];
  /** Core length at no drive and at full, in exit radii. */
  len: [number, number];
  /** Core sigma at the exit in exit radii, and its growth per unit length. */
  coreW: number;
  coreSpread: number;
  /** Side-on brightness of the core at the exit. */
  coreB: number;
  /** How fast the core dims down its length (e-folds over the whole length). */
  coreFade: number;
  haloW: number;
  haloSpread: number;
  haloB: number;
  /** Halo length over core length. */
  haloLen: number;
  haloFade: number;
  /** Ring radius and width, in exit radii, and its brightness. */
  ringR: number;
  ringB: number;
  throatB: number;
  knotB: number;
  knotCount: number;
  /** Relative flicker amplitude on the render clock. */
  flicker: number;
  /** How far the brightest light whitens toward the core's own white, 0..1. */
  white: number;
  /** What the long terms keep end-on, and the exit aura over the throat, when not END_ON and AURA_B. */
  endOn?: number;
  auraB?: number;
}

const SHAPES: Record<PlumeFamily, FamilyShape> = {
  // Canon: "thin faint violet plasma plumes (no bright torch)". The violet
  // sits in the halo at low brightness; the core is a pale blue-white, which
  // is what keeps ACES from pushing the whole plume to magenta.
  mpd: {
    core: [0.64, 0.68, 1.0], halo: [0.42, 0.3, 1.0], ring: [0.6, 0.56, 1.0], knot: [0, 0, 0],
    len: [5, 30], coreW: 0.3, coreSpread: 0.03, coreB: 0.6, coreFade: 2.2,
    haloW: 0.55, haloSpread: 0.075, haloB: 0.14, haloLen: 1.35, haloFade: 1.7,
    ringR: 0.7, ringB: 0.45, throatB: 0.65, knotB: 0, knotCount: 0, flicker: 0.03, white: 0.35,
  },
  nep: {
    core: [0.6, 0.68, 1.0], halo: [0.44, 0.48, 1.0], ring: [0.55, 0.62, 1.0], knot: [0, 0, 0],
    len: [5, 34], coreW: 0.7, coreSpread: 0.11, coreB: 0.3, coreFade: 2.3,
    haloW: 1.4, haloSpread: 0.2, haloB: 0.12, haloLen: 1.4, haloFade: 1.8,
    ringR: 0.55, ringB: 0.15, throatB: 0.3, knotB: 0, knotCount: 0, flicker: 0.025, white: 0.4,
  },
  fusion: {
    core: [0.76, 0.8, 1.0], halo: [0.38, 0.39, 1.0], ring: [0.66, 0.68, 1.0], knot: [0, 0, 0],
    len: [6, 36], coreW: 0.26, coreSpread: 0.024, coreB: 0.8, coreFade: 2.0,
    haloW: 0.5, haloSpread: 0.065, haloB: 0.16, haloLen: 1.35, haloFade: 1.6,
    ringR: 0.62, ringB: 0.7, throatB: 0.95, knotB: 0, knotCount: 0, flicker: 0.03, white: 0.85,
  },
  // The Pup's livery blue (0x6bb0ff) in the halo. A thin halo keeps its twin
  // bells two plumes for a third of the ship's length, and a low throat and
  // white keep the chase camera's end-on view from burning out the stern.
  dfd: {
    core: [0.62, 0.8, 1.0], halo: [0.16, 0.42, 1.0], ring: [0.45, 0.72, 1.0], knot: [0, 0, 0],
    len: [4, 26], coreW: 0.22, coreSpread: 0.018, coreB: 0.75, coreFade: 2.0,
    haloW: 0.45, haloSpread: 0.035, haloB: 0.18, haloLen: 1.45, haloFade: 1.5,
    ringR: 0.68, ringB: 0.6, throatB: 0.45, knotB: 0, knotCount: 0, flicker: 0.02, white: 0.3,
    endOn: 0.14, auraB: 0.15,
  },
  chem: {
    core: [1.0, 0.8, 0.58], halo: [1.0, 0.58, 0.32], ring: [1.0, 0.7, 0.45], knot: [1.0, 0.9, 0.74],
    len: [3, 14], coreW: 0.3, coreSpread: 0.05, coreB: 0.95, coreFade: 2.2,
    haloW: 0.8, haloSpread: 0.35, haloB: 0.12, haloLen: 1.25, haloFade: 1.8,
    ringR: 0, ringB: 0, throatB: 0.7, knotB: 0.5, knotCount: 4, flicker: 0.07, white: 0.8,
  },
  rcs: {
    core: [1.0, 0.94, 0.86], halo: [0.9, 0.86, 0.84], ring: [0, 0, 0], knot: [1.0, 0.95, 0.88],
    len: [5, 16], coreW: 0.35, coreSpread: 0.08, coreB: 1.1, coreFade: 2.0,
    haloW: 0.8, haloSpread: 0.3, haloB: 0.24, haloLen: 1.2, haloFade: 1.8,
    ringR: 0, ringB: 0, throatB: 0.7, knotB: 0.35, knotCount: 2, flicker: 0.08, white: 0.8,
  },
};

interface LookScale {
  gain: number;
  length: number;
  halo: number;
  /** The soft ceiling the summed brightness rolls off under. */
  emax: number;
  /** Aft point light, candela at full drive per unit of exit radius. */
  light: number;
}

const LOOKS: Record<PlumeLook, LookScale> = {
  subtle: { gain: 0.5, length: 0.7, halo: 1.1, emax: 0.8, light: 3 },
  cinematic: { gain: 1, length: 1, halo: 1, emax: 1.15, light: 8 },
  vivid: { gain: 1.6, length: 1.15, halo: 1.2, emax: 2.2, light: 14 },
};

/**
 * `?plumetint=magenta`: every term in the saturated purple the complaint's
 * screenshot shows at the old needle's tip (sRGB 142 27 186), as linear light.
 */
const MAGENTA_CONTROL: [number, number, number] = [0.45, 0.02, 1.0];

const N_CORE = 6;
const N_HALO = 5;
const N_KNOT = 4;
/** Proxy reach in sigmas: past it a term is under 0.6% of its peak. */
const REACH = 3.2;
const SQRT_2PI = Math.sqrt(2 * Math.PI);
/** What the long terms keep looked at straight down the axis (see the shader). */
const END_ON = 0.3;
/** The exit aura's width in exit radii, and its brightness over the throat's. */
const AURA_W = 1.8;
const AURA_B = 0.35;
/**
 * What an ignition's flash at 1 adds to each term, over its steady brightness:
 * the throat and the wide aura carry the pop, the core a little of it.
 */
const FLASH_THROAT = 2.5;
const FLASH_AURA = 2.0;
const FLASH_RING = 1.5;
const FLASH_CORE = 0.6;
/** First-bead axial sigma of the core and halo chains, in exit radii. */
const CORE_S0 = 0.35;
const HALO_S0 = 0.6;

/**
 * Where a chain's beads sit down the axis and how long each is. The first
 * sits three of its own sigmas out of the exit, so nothing spills upstream
 * into the nozzle, and each is longer than the last by one ratio, found here,
 * that lands the final bead at 80% of `len`: short beads where the plume
 * changes fastest, long ones in the tail. `d` is each bead's spacing to its
 * neighbours, which is what the brightness is normalised by.
 */
export function beadLayout(n: number, len: number, s0: number): { z: number[]; s: number[]; d: number[] } {
  const target = 0.8 * len;
  const reach = (q: number, sig: number): number => {
    let z = 3 * sig;
    let sk = sig;
    for (let k = 1; k < n; k++) { const next = sk * q; z += 1.6 * Math.sqrt(sk * next); sk = next; }
    return z;
  };
  let sig = s0;
  let q = 1;
  if (reach(1, sig) >= target) sig = target / (3 + 1.6 * (n - 1));
  else {
    let lo = 1;
    let hi = 4;
    for (let i = 0; i < 30; i++) { q = (lo + hi) / 2; if (reach(q, sig) < target) lo = q; else hi = q; }
  }
  const z: number[] = [];
  const s: number[] = [];
  let zk = 3 * sig;
  let sk = sig;
  for (let k = 0; k < n; k++) {
    z.push(zk); s.push(sk);
    const next = sk * q;
    zk += 1.6 * Math.sqrt(sk * next);
    sk = next;
  }
  const d = z.map((zk2, k) => {
    const prev = k > 0 ? zk2 - z[k - 1]! : 1.6 * s[0]!;
    const nxt = k < n - 1 ? z[k + 1]! - zk2 : 1.6 * s[k]!;
    return (prev + nxt) / 2;
  });
  return { z, s, d };
}

const VERT = /* glsl */ `
#include <common>
#include <logdepthbuf_pars_vertex>
varying vec3 vObj;
varying vec3 vCam;
void main() {
  vObj = position;
  vCam = (inverse(modelMatrix) * vec4(cameraPosition, 1.0)).xyz;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  #include <logdepthbuf_vertex>
}
`;

const FRAG = /* glsl */ `
#include <common>
#include <logdepthbuf_pars_fragment>
// The vertex stage's own matrices, which three.js sets per object by name.
uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
uniform vec4 uCore[${N_CORE}];
uniform vec4 uHalo[${N_HALO}];
uniform vec4 uKnot[${N_KNOT}];
uniform vec4 uThroat;
uniform vec4 uAura;
uniform vec4 uRing;
uniform float uRingSz;
uniform vec3 uCoreCol;
uniform vec3 uHaloCol;
uniform vec3 uRingCol;
uniform vec3 uKnotCol;
uniform vec3 uBox;
uniform float uEmax;
uniform float uWhite;
uniform float uEndOn;
uniform float uHard;
uniform vec3 uHardCol;
varying vec3 vObj;
varying vec3 vCam;

float plErf(float x) {
  float s = sign(x);
  x = abs(x);
  float t = 1.0 / (1.0 + 0.3275911 * x);
  float y = 1.0 - (((((1.061405429 * t - 1.453152027) * t) + 1.421413741) * t - 0.284496736) * t
    + 0.254829592) * t * exp(-x * x);
  return s * y;
}

// The integral of exp(-(r^2/sr^2 + (z-zc)^2/sz^2)/2) along ro + t*rd, t in [t0, t1].
float plGauss(vec3 ro, vec3 rd, float t0, float t1, float zc, float sr, float sz) {
  float ir = 1.0 / (sr * sr);
  float iz = 1.0 / (sz * sz);
  float oz = ro.z - zc;
  float a = 0.5 * ((rd.x * rd.x + rd.y * rd.y) * ir + rd.z * rd.z * iz);
  float b = (ro.x * rd.x + ro.y * rd.y) * ir + oz * rd.z * iz;
  float c = 0.5 * ((ro.x * ro.x + ro.y * ro.y) * ir + oz * oz * iz);
  float m = max(c - b * b / (4.0 * a), 0.0);
  if (m > 11.0) return 0.0;
  float sa = sqrt(a);
  float h = b / (2.0 * a);
  return 0.8862269 / sa * exp(-m) * (plErf(sa * (t1 + h)) - plErf(sa * (t0 + h)));
}

void main() {
  vec3 ro = vCam;
  vec3 d = vObj - ro;
  float t1 = length(d);
  vec3 rd = d / t1;
  vec3 safe = vec3(abs(rd.x) < 1e-6 ? 1e-6 : rd.x, abs(rd.y) < 1e-6 ? 1e-6 : rd.y,
    abs(rd.z) < 1e-6 ? 1e-6 : rd.z);
  vec3 ta = (vec3(-uBox.x, -uBox.x, uBox.y) - ro) / safe;
  vec3 tb = (vec3(uBox.x, uBox.x, uBox.z) - ro) / safe;
  vec3 tn = min(ta, tb);
  float t0 = clamp(max(max(tn.x, tn.y), tn.z), 0.0, t1);

  vec3 col;
  if (uHard > 0.5) {
    col = uHardCol;
  } else {
    float core = 0.0;
    for (int i = 0; i < ${N_CORE}; i++) {
      vec4 g = uCore[i];
      if (g.w > 0.0) core += g.w * plGauss(ro, rd, t0, t1, g.x, g.y, g.z);
    }
    float halo = 0.0;
    for (int i = 0; i < ${N_HALO}; i++) {
      vec4 g = uHalo[i];
      if (g.w > 0.0) halo += g.w * plGauss(ro, rd, t0, t1, g.x, g.y, g.z);
    }
    float knot = 0.0;
    for (int i = 0; i < ${N_KNOT}; i++) {
      vec4 g = uKnot[i];
      if (g.w > 0.0) knot += g.w * plGauss(ro, rd, t0, t1, g.x, g.y, g.z);
    }
    float throat = uThroat.w > 0.0 ? uThroat.w * plGauss(ro, rd, t0, t1, uThroat.x, uThroat.y, uThroat.z) : 0.0;
    float aura = uAura.w > 0.0 ? uAura.w * plGauss(ro, rd, t0, t1, uAura.x, uAura.y, uAura.z) : 0.0;
    float ring = 0.0;
    if (uRing.w > 0.0) {
      ring = max(uRing.w * (plGauss(ro, rd, t0, t1, uRing.x, uRing.z, uRingSz)
        - plGauss(ro, rd, t0, t1, uRing.x, uRing.y, uRingSz)), 0.0);
    }
    // Optically thin gas is brightest looked at down its own length, where the
    // ray crosses every bead; that is the torch this module exists to avoid,
    // so the long terms are eased back as the view comes onto the axis. The
    // exit's own terms are not, which is what lets the rings read end-on.
    float endOn = mix(1.0, uEndOn, smoothstep(0.55, 0.98, abs(rd.z)));
    col = uCoreCol * (core * endOn + throat) + uHaloCol * halo * endOn + uRingCol * ring + uKnotCol * knot * endOn
      + mix(uCoreCol, uHaloCol, 0.5) * aura;
    float l = dot(col, vec3(0.2126, 0.7152, 0.0722));
    if (l > 1e-6) {
      float ls = uEmax * (1.0 - exp(-l / uEmax));
      col *= ls / l;
      col = mix(col, vec3(ls), smoothstep(0.45 * uEmax, uEmax, ls) * uWhite);
      // A sub-LSB dither so the faint halo's long gradient does not band.
      float n = fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453);
      col += (n - 0.5) * 0.002 * smoothstep(0.002, 0.02, l);
    }
  }

  // Depth at the ray's entry into the plume's box, not at the proxy's back
  // face: the hull in front of the gas hides it, the hull behind does not.
  vec4 vp = modelViewMatrix * vec4(ro + rd * t0, 1.0);
#ifdef USE_LOGDEPTHBUF
  gl_FragDepth = log2(max(1.0 - vp.z, 1.0)) * logDepthBufFC * 0.5;
#else
  vec4 cp = projectionMatrix * vp;
  gl_FragDepth = clamp(0.5 * cp.z / cp.w + 0.5, 0.0, 1.0);
#endif
  gl_FragColor = vec4(max(col, vec3(0.0)), 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}
`;

export interface DrivePlumeConfig {
  family: PlumeFamily;
  /** Nozzle (or cluster) exit radius, in the parent's local units. */
  exitRadius: number;
  look?: PlumeLook;
  /** Phase offset for the flicker, so two plumes never breathe in step. */
  seed?: number;
  /** `?plumehard=1`: a flat hard-edged frustum, the control the edge bar must fail. */
  hard?: boolean;
  /** `?plumetint=magenta`: every term in the complaint screenshot's purple. */
  magenta?: boolean;
}

/**
 * A burn's start and end, on the render clock. `flash` is the ignition, 1 at
 * first light and decaying; `grow` how far the plume has run out of the exit
 * since then, [0, 1]. After cut-off `front` is how far down the plume, over its
 * own length, the last gas has left the exit behind, and `fade` what is left
 * of its light.
 */
export interface PlumeFx {
  flash: number;
  grow: number;
  front: number;
  fade: number;
}
export const STEADY_FX: Readonly<PlumeFx> = { flash: 0, grow: 1, front: 0, fade: 1 };

export interface DrivePlume {
  /** Exit at the origin, venting along +Z. Aim it with its quaternion. */
  mesh: THREE.Mesh;
  /** Drive level [0, 1], the render clock in seconds, and where the burn is in its start or end. */
  update: (level: number, timeS: number, fx?: Readonly<PlumeFx>) => void;
  /** The drawn core length, the halo radius at its end, and the overall gain. */
  lengthLocal: () => number;
  radiusLocal: () => number;
  brightness: () => number;
  family: PlumeFamily;
  exitRadius: number;
  /** Linear colour of the light the plume throws on the hull. */
  lightColor: THREE.Color;
  /** Candela the aft light owes this plume at full drive. */
  lightAtFull: number;
  dispose: () => void;
}

/** Brightness at a drive level. Steeper than linear: the plume also lengthens with the drive, and seen down its axis a quarter burn should read a quarter of a full one (thruster-check.mjs). */
export function plumeGain(level: number): number {
  return level <= 0 ? 0 : Math.pow(Math.min(level, 1), 1.6);
}

export function createDrivePlume(config: DrivePlumeConfig): DrivePlume {
  const shape = SHAPES[config.family];
  const look = LOOKS[config.look ?? 'cinematic'];
  const R = config.exitRadius > 0 ? config.exitRadius : 0.1;
  const seed = config.seed ?? 0;
  const hard = config.hard === true;
  const tint = (c: [number, number, number]): THREE.Vector3 =>
    config.magenta ? new THREE.Vector3(...MAGENTA_CONTROL) : new THREE.Vector3(...c);

  const lenFull = shape.len[1] * look.length * R;
  const lenMin = shape.len[0] * look.length * R;
  const haloLenFull = lenFull * shape.haloLen;
  const haloSigmaEnd = shape.haloW * R + shape.haloSpread * haloLenFull;
  // The proxy: a frustum from just upstream of the exit to past the halo's last
  // bead, as wide as the widest term it holds at each end.
  const zMin = -1.5 * R;
  const lastHalo = beadLayout(N_HALO, haloLenFull, HALO_S0 * R);
  const zMax = lastHalo.z[N_HALO - 1]! + REACH * lastHalo.s[N_HALO - 1]!;
  const rNear = REACH * Math.max(shape.haloW * R, (shape.ringR + 0.5) * R * 0.75, shape.coreW * R, AURA_W * R);
  const rFar = REACH * (shape.haloW * R + shape.haloSpread * zMax);
  const geometry = new THREE.CylinderGeometry(rFar, rNear, zMax - zMin, 20, 1, false);
  geometry.rotateX(Math.PI / 2);
  geometry.translate(0, 0, (zMin + zMax) / 2);

  const vec4s = (n: number): THREE.Vector4[] => Array.from({ length: n }, () => new THREE.Vector4());
  const uniforms = {
    uCore: { value: vec4s(N_CORE) },
    uHalo: { value: vec4s(N_HALO) },
    uKnot: { value: vec4s(N_KNOT) },
    uThroat: { value: new THREE.Vector4() },
    uAura: { value: new THREE.Vector4() },
    uRing: { value: new THREE.Vector4() },
    uRingSz: { value: 0.25 * R },
    uCoreCol: { value: tint(shape.core) },
    uHaloCol: { value: tint(shape.halo) },
    uRingCol: { value: tint(shape.ring) },
    uKnotCol: { value: tint(shape.knot) },
    uBox: { value: new THREE.Vector3(Math.max(rNear, rFar), zMin, zMax) },
    uEmax: { value: look.emax },
    uWhite: { value: config.magenta ? 0 : shape.white },
    uEndOn: { value: shape.endOn ?? END_ON },
    uHard: { value: hard ? 1 : 0 },
    uHardCol: { value: new THREE.Vector3() },
  };
  const material = new THREE.ShaderMaterial({
    name: `drive-plume-${config.family}`,
    vertexShader: VERT,
    fragmentShader: FRAG,
    uniforms,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: THREE.AdditiveBlending,
    side: THREE.BackSide,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.renderOrder = 2;

  let length = lenMin;
  let gainNow = 0;
  let haloRadius = shape.haloW * R;

  function update(level: number, timeS: number, fx: Readonly<PlumeFx> = STEADY_FX): void {
    const lv = Math.min(Math.max(level, 0), 1);
    const g = plumeGain(lv) * look.gain * fx.fade;
    gainNow = g;
    const ph = seed * 2.399;
    const flick = 1 + shape.flicker * (0.6 * Math.sin(timeS * 71.0 + ph) + 0.4 * Math.sin(timeS * 112.5 + ph * 2.1));
    length = (lenMin + (lenFull - lenMin) * lv) * (1 + 0.25 * shape.flicker * Math.sin(timeS * 9.3 + ph))
      * (0.1 + 0.9 * fx.grow);
    const haloLen = length * shape.haloLen;
    haloRadius = 2 * (shape.haloW * R + shape.haloSpread * haloLen);

    if (hard) {
      const c = new THREE.Vector3(...(config.magenta ? MAGENTA_CONTROL : shape.halo)).lerp(
        new THREE.Vector3(...(config.magenta ? MAGENTA_CONTROL : shape.core)), 0.5);
      uniforms.uHardCol.value.copy(c.multiplyScalar(0.35 * g));
      return;
    }

    // Past cut-off the exit has stopped feeding the plume: every bead the last
    // gas has left behind goes out, so the burn leaves the bell rather than
    // shrinking back into it.
    const frontZ = fx.front * haloLen;
    const chain = (out: THREE.Vector4[], n: number, len: number, w: number, spread: number,
      b: number, fade: number, amp: number, s0: number): void => {
      const beads = beadLayout(n, len, s0);
      for (let k = 0; k < n; k++) {
        const zc = beads.z[k]!;
        const u = zc / len;
        const sr = w * R + spread * zc;
        const left = frontZ > 0 ? THREE.MathUtils.smoothstep(zc, frontZ - beads.s[k]!, frontZ + beads.s[k]!) : 1;
        const side = b * Math.exp(-fade * u) * amp * left;
        // `side` is what the chain adds seen side-on through its axis at this
        // bead; the spacing term takes out the overlap of its neighbours.
        const overlap = (beads.s[k]! * SQRT_2PI) / beads.d[k]!;
        out[k]!.set(zc, sr, beads.s[k]!, side > 0 ? side / (sr * SQRT_2PI * overlap) : 0);
      }
    };
    chain(uniforms.uCore.value, N_CORE, length, shape.coreW, shape.coreSpread, shape.coreB, shape.coreFade,
      g * flick * (1 + FLASH_CORE * fx.flash), CORE_S0 * R);
    chain(uniforms.uHalo.value, N_HALO, haloLen, shape.haloW, shape.haloSpread, shape.haloB * look.halo,
      shape.haloFade, g, HALO_S0 * R);

    // The exit's own terms are the source: lit at ignition, out at cut-off.
    const exitG = fx.front > 0 ? 0 : g;
    const throatSr = 0.5 * R;
    uniforms.uThroat.value.set(0.12 * R, throatSr, 0.35 * R,
      (shape.throatB * exitG * flick * (1 + FLASH_THROAT * fx.flash)) / (throatSr * SQRT_2PI));
    // A wide faint glow round the exit: what keeps a plume seen end-on from
    // afar a soft light rather than a hard point.
    const auraSr = AURA_W * R;
    uniforms.uAura.value.set(1.2 * R, auraSr, 1.4 * R,
      ((shape.auraB ?? AURA_B) * shape.throatB * exitG * (1 + FLASH_AURA * fx.flash)) / (auraSr * SQRT_2PI));
    if (shape.ringB > 0) {
      const s1 = 0.62 * shape.ringR * R;
      const s2 = 1.0 * shape.ringR * R;
      uniforms.uRing.value.set(0.08 * R, s1, s2, (shape.ringB * exitG * (1 + FLASH_RING * fx.flash)) / (s2 * SQRT_2PI));
    } else uniforms.uRing.value.set(0, 1, 1, 0);

    for (let k = 0; k < N_KNOT; k++) {
      const v = uniforms.uKnot.value[k]!;
      if (k >= shape.knotCount || shape.knotB <= 0) { v.set(0, 1, 1, 0); continue; }
      const spacing = length / (shape.knotCount + 1.5);
      const zc = spacing * (k + 0.8) * (1 + 0.02 * Math.sin(timeS * 5.1 + k + ph));
      const sr = 0.22 * R + 0.02 * zc;
      const side = shape.knotB * exitG * flick * Math.exp(-1.1 * k);
      v.set(zc, sr, 0.3 * sr + 0.08 * spacing, side / (sr * SQRT_2PI));
    }
  }

  update(0, 0);
  const lightColor = new THREE.Color().setRGB(
    (shape.core[0] + shape.halo[0]) / 2, (shape.core[1] + shape.halo[1]) / 2, (shape.core[2] + shape.halo[2]) / 2,
    THREE.LinearSRGBColorSpace);

  return {
    mesh,
    update,
    lengthLocal: () => length,
    radiusLocal: () => haloRadius,
    brightness: () => gainNow,
    family: config.family,
    exitRadius: R,
    lightColor,
    lightAtFull: look.light * R * (shape.coreB + shape.throatB),
    dispose: () => {
      geometry.dispose();
      material.dispose();
      mesh.removeFromParent();
    },
  };
}

// ---- which engine each nozzle is ----

export interface NozzleClass {
  family: PlumeFamily;
  exitRadius: number;
  /** A sprint booster: drawn only when the drive's `boost` asks for it. */
  booster: boolean;
}

/**
 * Hull-local exit radii read off the hull builders (tools/parts/hulls/, builder
 * metres over ten): the Tower's MPD drum 2.8 m, the Cannibal's cups 0.6 m and
 * its booster bells 0.72 m, the Spine & Truss heads 1.95 m, the Lance's MPD
 * clusters 2.55 m and its booster bells 1.02 m. A row with no radius takes its
 * nozzle's own plume attach `radius`: the Pup is built at final size.
 */
const AUTHORED_DRIVES: Record<string, { main: [PlumeFamily, number?]; sprint?: [PlumeFamily, number?] }> = {
  tower: { main: ['mpd', 0.28] },
  pup: { main: ['dfd'] },
  cannibal: { main: ['nep', 0.06], sprint: ['chem', 0.072] },
  spinetruss: { main: ['fusion', 0.195] },
  lance: { main: ['fusion', 0.255], sprint: ['chem', 0.1] },
};

/** The procedural drive's plume cone is 0.16 wide in a bell that opens to 0.275. */
const PROCEDURAL_EXIT_OVER_CONE = 1.72;

/**
 * Classifies a hull's drive nozzles, found by where they sit (hull-local) and
 * the radius of the cone the hull drew there. A nozzle the manifest names
 * `sprint-*` or `boost*` is a chemical booster.
 */
export function nozzleClassifier(spec: ShipSpec | undefined): (at: THREE.Vector3, coneRadius: number) => NozzleClass {
  const table = spec ? AUTHORED_DRIVES[spec.key] : undefined;
  const attach = spec?.hull?.manifest.attach.filter((a) => a.role === 'plume') ?? [];
  return (at, coneRadius) => {
    let near: (typeof attach)[number] | undefined;
    let best = Infinity;
    for (const a of attach) {
      const d = Math.hypot(a.at[0] - at.x, a.at[1] - at.y, a.at[2] - at.z);
      if (d < best) { best = d; near = a; }
    }
    const booster = best < 0.05 && /^(sprint|boost)/.test(near?.id ?? '');
    if (table) {
      const row = booster && table.sprint ? table.sprint : table.main;
      return { family: row[0], exitRadius: row[1] ?? near?.radius ?? coneRadius, booster };
    }
    if (booster) return { family: 'chem', exitRadius: coneRadius, booster };
    return { family: 'fusion', exitRadius: attach.length ? coneRadius : coneRadius * PROCEDURAL_EXIT_OVER_CONE, booster };
  };
}

/**
 * Nozzles closer than this many exit radii centre to centre draw as one
 * cluster plume. 2.2 and not more: the Tower's two MPD drums sit 2.4 radii
 * apart and are two plumes in canon, while the Cannibal's cups sit 1.9 apart.
 */
export const CLUSTER_MERGE_RADII = 2.2;

export interface PlumeSite {
  at: THREE.Vector3;
  dir: THREE.Vector3;
  cls: NozzleClass;
}

/** Groups nozzles of one family into cluster plumes: centroid, mean axis, and a radius that covers them. */
export function clusterNozzles(sites: PlumeSite[]): PlumeSite[] {
  const parent = sites.map((_, i) => i);
  const find = (i: number): number => (parent[i] === i ? i : (parent[i] = find(parent[i]!)));
  for (let i = 0; i < sites.length; i++) {
    for (let j = i + 1; j < sites.length; j++) {
      const a = sites[i]!;
      const b = sites[j]!;
      if (a.cls.family !== b.cls.family || a.cls.booster !== b.cls.booster) continue;
      const r = (a.cls.exitRadius + b.cls.exitRadius) / 2;
      if (a.at.distanceTo(b.at) < CLUSTER_MERGE_RADII * r) parent[find(i)] = find(j);
    }
  }
  const groups = new Map<number, PlumeSite[]>();
  sites.forEach((s, i) => {
    const k = find(i);
    const g = groups.get(k);
    if (g) g.push(s); else groups.set(k, [s]);
  });
  return [...groups.values()].map((g) => {
    if (g.length === 1) return g[0]!;
    const at = new THREE.Vector3();
    const dir = new THREE.Vector3();
    for (const s of g) { at.add(s.at); dir.add(s.dir); }
    at.multiplyScalar(1 / g.length);
    dir.normalize();
    let reach = 0;
    for (const s of g) reach = Math.max(reach, s.at.distanceTo(at) + s.cls.exitRadius);
    return { at, dir, cls: { ...g[0]!.cls, exitRadius: reach } };
  });
}
